import { stringify } from '@0xtorch/core'
import type { TransactionDecoded } from '@pkg/evm'

/** 補助ログの送信パラメータ。認証は送信を開始するときに取得する。 */
type SaveParameters = {
  endpoint: string
  getToken: () => Promise<string | null>
  transaction: TransactionDecoded
}

/**
 * 補助ログを有限のキューで直列送信する。混雑時はログ送信を省略して同期を優先する。
 * @param params HTTP 送信、時計、待機処理。テストでは外部通信を差し替える。
 * @returns 送信完了を返す関数。障害が発生しても後続のキューを停止させない。
 */
export const createTransactionLogSaver = ({
  fetchImplementation = ({ url, init }: { url: string; init: RequestInit }) =>
    fetch(url, init),
  now = Date.now,
  sleepMs = async ({ ms }: { ms: number }) => {
    await new Promise((resolve) => setTimeout(resolve, ms))
  },
}: {
  fetchImplementation?: (params: {
    url: string
    init: RequestInit
  }) => Promise<Response>
  now?: () => number
  sleepMs?: (params: { ms: number }) => Promise<void>
} = {}) => {
  let queue = Promise.resolve()
  let nextStart = 0
  const pending = new Set<string>()
  return async ({
    endpoint,
    getToken,
    transaction,
  }: SaveParameters): Promise<void> => {
    const key = `${endpoint}:${transaction.chainId}:${transaction.hash}`
    if (pending.size >= 20 || pending.has(key)) return
    pending.add(key)
    const job = queue
      .then(async () => {
        const token = await getToken()
        if (token === null) return
        if (nextStart > now()) await sleepMs({ ms: nextStart - now() })
        nextStart = now() + 500
        const response = await fetchImplementation({
          url: new URL('/v1/log/evm-transaction', endpoint).toString(),
          init: {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: stringify(transaction),
            signal: AbortSignal.timeout(10_000),
          },
        })
        await response.body?.cancel()
        if (!response.ok)
          throw new Error(`Failed to save transaction log: ${response.status}`)
      })
      .finally(() => {
        pending.delete(key)
      })
    queue = job.catch(() => {})
    await job
  }
}

/** アプリ全体で補助ログの送信数と送信間隔を共有する。 */
export const saveTransactionLog = createTransactionLogSaver()
